// src/pages/ExecutiveDashboard.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAllDuesDashboard } from '../api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import NavBar from '../components/shared/NavBar';

const NAVY  = '#1B3A6B';
const GOLD  = '#C9A84C';
const RED   = '#c0392b';
const GREEN = '#1a6b3a';

const CHART_COLORS = [
  '#1B3A6B', '#C9A84C', '#2ecc71', '#e74c3c',
  '#9b59b6', '#3498db', '#e67e22', '#1abc9c',
  '#e91e63', '#00bcd4', '#ff5722', '#607d8b',
  '#795548', '#9c27b0'
];

export default function ExecutiveDashboard() {
  const navigate    = useNavigate();
  const { role }    = useAuth();

  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Individual drill-down search
  const [search,   setSearch]   = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (role !== 'executive' && role !== 'treasurer') {
      navigate('/unauthorized');
      return;
    }
    loadDashboard();
  }, [role, navigate]);

  async function loadDashboard() {
    setLoading(true);
    try {
      const result = await getAllDuesDashboard();
      setData(result);
    } catch (err) {
      setError('Could not load dashboard: ' + err.message);
    } finally {
      setLoading(false);
    }
  }


  // ── Computed values ───────────────────────────────────────
  const totalOwed    = data
    ? Object.values(data.byHouse || {})
        .reduce((s, h) => s + (h.owed || 0), 0)
    : 0;

  const totalPaid    = data
    ? Object.values(data.byHouse || {})
        .reduce((s, h) => s + (h.paid || 0), 0)
    : 0;

  const totalBalance = totalOwed - totalPaid;
  const collectionRate = totalOwed > 0
    ? Math.round((totalPaid / totalOwed) * 100) : 0;

  // House chart data
  const houseChartData = data
    ? Object.entries(data.byHouse || {})
        .map(([house, d]) => ({
          house: house.length > 8
            ? house.substring(0, 8) + '...' : house,
          fullHouse: house,
          Owed: d.owed || 0,
          Paid: d.paid || 0,
          Rate: d.owed > 0
            ? Math.round((d.paid / d.owed) * 100) : 0
        }))
        .sort((a, b) => b.Rate - a.Rate)
    : [];

  // Year chart data
  const yearChartData = data
    ? Object.entries(data.byYear || {})
        .map(([year, d]) => ({
          year,
          Owed: d.owed || 0,
          Paid: d.paid || 0,
          Balance: (d.owed || 0) - (d.paid || 0)
        }))
        .sort((a, b) => a.year - b.year)
    : [];

  // Country chart data
  const countryChartData = data
    ? Object.entries(data.byCountry || {})
        .map(([country, d]) => ({
          country,
          Paid: d.paid || 0,
          Owed: d.owed || 0
        }))
        .sort((a, b) => b.Paid - a.Paid)
        .slice(0, 10)
    : [];

  // Individual search
  const filteredIndividuals = data && search.trim()
    ? (data.individual || [])
        .filter(m =>
          m.name?.toLowerCase().includes(search.toLowerCase()) ||
          m.boardingHouse?.toLowerCase()
            .includes(search.toLowerCase()) ||
          m.location?.toLowerCase().includes(search.toLowerCase())
        )
        .slice(0, 20)
    : [];

  // Best and worst performing houses
  const bestHouse = houseChartData.length > 0
    ? houseChartData[0] : null;
  const worstHouse = houseChartData.length > 0
    ? houseChartData[houseChartData.length - 1] : null;

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: '#f0f4f8', fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>
            📊
          </div>
          <p style={{ color: '#666' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: '#f0f4f8',
      fontFamily: 'Arial, sans-serif'
    }}>

      {/* Nav */}
      <NavBar />

      <div style={{
        maxWidth: '1100px', margin: '0 auto',
        padding: '24px 16px'
      }}>

        {/* Page Header */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
          borderRadius: '12px', padding: '24px',
          marginBottom: '20px', color: '#fff',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: '12px'
        }}>
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: '22px' }}>
              📊 Executive Dashboard
            </h2>
            <p style={{ margin: 0, opacity: 0.85, fontSize: '14px' }}>
              Dues collection overview — all houses & members
            </p>
          </div>
          <button
            onClick={loadDashboard}
            style={{
              background: GOLD, color: NAVY, border: 'none',
              borderRadius: '8px', padding: '8px 16px',
              cursor: 'pointer', fontWeight: 'bold',
              fontSize: '13px'
            }}
          >
            🔄 Refresh
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#fdecea', border: '1px solid #f5c6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: RED, fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* KPI Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '16px', marginBottom: '20px'
        }}>
          {[
            {
              label: 'Total Owed',
              value: totalOwed.toLocaleString(),
              icon: '📋', bg: '#fff', color: NAVY
            },
            {
              label: 'Total Collected',
              value: totalPaid.toLocaleString(),
              icon: '✅', bg: '#d4edda', color: GREEN
            },
            {
              label: 'Outstanding',
              value: totalBalance.toLocaleString(),
              icon: '⏳',
              bg: totalBalance > 0 ? '#fdecea' : '#d4edda',
              color: totalBalance > 0 ? RED : GREEN
            },
            {
              label: 'Collection Rate',
              value: `${collectionRate}%`,
              icon: '📈',
              bg: collectionRate >= 75 ? '#d4edda'
                : collectionRate >= 50 ? '#fff3cd' : '#fdecea',
              color: collectionRate >= 75 ? GREEN
                : collectionRate >= 50 ? '#856404' : RED
            },
            {
              label: 'Best House',
              value: bestHouse?.fullHouse || '-',
              icon: '🏆', bg: '#fff3cd', color: '#856404'
            },
            {
              label: 'Needs Attention',
              value: worstHouse?.fullHouse || '-',
              icon: '⚠️', bg: '#fdecea', color: RED
            }
          ].map(card => (
            <div key={card.label} style={{
              background: card.bg, borderRadius: '12px',
              padding: '20px', textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>
                {card.icon}
              </div>
              <div style={{
                fontSize: '20px', fontWeight: 'bold',
                color: card.color, marginBottom: '4px'
              }}>
                {card.value}
              </div>
              <div style={{
                fontSize: '11px', color: '#888',
                fontWeight: '600', textTransform: 'uppercase'
              }}>
                {card.label}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: '8px',
          marginBottom: '20px', flexWrap: 'wrap'
        }}>
          {[
            { id: 'overview',    label: '📊 By Year' },
            { id: 'houses',      label: '🏠 By House' },
            { id: 'countries',   label: '🌍 By Country' },
            { id: 'individuals', label: '👤 Individual' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 20px',
                background: activeTab === tab.id ? NAVY : '#fff',
                color: activeTab === tab.id ? '#fff' : NAVY,
                border: `2px solid ${NAVY}`,
                borderRadius: '8px', cursor: 'pointer',
                fontSize: '13px', fontWeight: '600'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── BY YEAR TAB ── */}
        {activeTab === 'overview' && (
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <h3 style={{
              color: NAVY, margin: '0 0 20px', fontSize: '16px'
            }}>
              Dues Collection by Year
            </h3>
            {yearChartData.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center' }}>
                No dues data available.
              </p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={yearChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => value.toLocaleString()}
                    />
                    <Legend />
                    <Bar dataKey="Owed"    fill={NAVY} />
                    <Bar dataKey="Paid"    fill={GOLD} />
                    <Bar dataKey="Balance" fill={RED}  />
                  </BarChart>
                </ResponsiveContainer>

                {/* Year table */}
                <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%', borderCollapse: 'collapse',
                  marginTop: '24px', fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      {['Year','Owed','Paid','Balance','Rate'].map(h => (
                        <th key={h} style={{
                          padding: '10px 16px', textAlign: 'left',
                          color: '#888', fontWeight: '600',
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          borderBottom: '1px solid #f0f0f0'
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {yearChartData
                      .sort((a, b) => b.year - a.year)
                      .map((row, i) => {
                        const rate = row.Owed > 0
                          ? Math.round((row.Paid / row.Owed) * 100)
                          : 0;
                        return (
                          <tr key={i} style={{
                            borderBottom: '1px solid #f0f0f0'
                          }}>
                            <td style={{
                              padding: '12px 16px',
                              fontWeight: 'bold', color: NAVY
                            }}>
                              {row.year}
                            </td>
                            <td style={{
                              padding: '12px 16px', color: '#333'
                            }}>
                              {row.Owed.toLocaleString()}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              color: GREEN, fontWeight: '600'
                            }}>
                              {row.Paid.toLocaleString()}
                            </td>
                            <td style={{
                              padding: '12px 16px',
                              color: row.Balance > 0 ? RED : GREEN,
                              fontWeight: '600'
                            }}>
                              {row.Balance.toLocaleString()}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center', gap: '8px'
                              }}>
                                <div style={{
                                  flex: 1, height: '8px',
                                  background: '#f0f0f0',
                                  borderRadius: '4px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    width: `${rate}%`, height: '100%',
                                    background: rate >= 75 ? GREEN
                                      : rate >= 50 ? GOLD : RED,
                                    borderRadius: '4px'
                                  }} />
                                </div>
                                <span style={{
                                  fontSize: '12px', fontWeight: 'bold',
                                  color: rate >= 75 ? GREEN
                                    : rate >= 50 ? '#856404' : RED,
                                  minWidth: '36px'
                                }}>
                                  {rate}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── BY HOUSE TAB ── */}
        {activeTab === 'houses' && (
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <h3 style={{
              color: NAVY, margin: '0 0 20px', fontSize: '16px'
            }}>
              Collection Rate by Boarding House
            </h3>
            {houseChartData.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center' }}>
                No house data available.
              </p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={houseChartData}
                    layout="vertical"
                    margin={{ left: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="house" type="category"
                      width={80} fontSize={12}
                    />
                    <Tooltip
                      formatter={(value) => value.toLocaleString()}
                    />
                    <Legend />
                    <Bar dataKey="Paid" fill={GOLD} />
                    <Bar dataKey="Owed" fill={NAVY} />
                  </BarChart>
                </ResponsiveContainer>

                {/* House table */}
                <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%', borderCollapse: 'collapse',
                  marginTop: '24px', fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      {['House','Owed','Paid','Balance','Rate'].map(h => (
                        <th key={h} style={{
                          padding: '10px 16px', textAlign: 'left',
                          color: '#888', fontWeight: '600',
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          borderBottom: '1px solid #f0f0f0'
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {houseChartData.map((row, i) => (
                      <tr key={i} style={{
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <td style={{
                          padding: '12px 16px',
                          fontWeight: 'bold', color: NAVY
                        }}>
                          {row.fullHouse}
                        </td>
                        <td style={{
                          padding: '12px 16px', color: '#333'
                        }}>
                          {row.Owed.toLocaleString()}
                        </td>
                        <td style={{
                          padding: '12px 16px',
                          color: GREEN, fontWeight: '600'
                        }}>
                          {row.Paid.toLocaleString()}
                        </td>
                        <td style={{
                          padding: '12px 16px',
                          color: (row.Owed - row.Paid) > 0
                            ? RED : GREEN,
                          fontWeight: '600'
                        }}>
                          {(row.Owed - row.Paid).toLocaleString()}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center', gap: '8px'
                          }}>
                            <div style={{
                              flex: 1, height: '8px',
                              background: '#f0f0f0',
                              borderRadius: '4px', overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${row.Rate}%`, height: '100%',
                                background: row.Rate >= 75 ? GREEN
                                  : row.Rate >= 50 ? GOLD : RED,
                                borderRadius: '4px'
                              }} />
                            </div>
                            <span style={{
                              fontSize: '12px', fontWeight: 'bold',
                              color: row.Rate >= 75 ? GREEN
                                : row.Rate >= 50 ? '#856404' : RED,
                              minWidth: '36px'
                            }}>
                              {row.Rate}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── BY COUNTRY TAB ── */}
        {activeTab === 'countries' && (
          <div className="exec-country-grid" style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px'
          }}>
            {/* Bar chart */}
            <div style={{
              background: '#fff', borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}>
              <h3 style={{
                color: NAVY, margin: '0 0 20px', fontSize: '16px'
              }}>
                Top 10 Countries by Collection
              </h3>
              {countryChartData.length === 0 ? (
                <p style={{ color: '#888', textAlign: 'center' }}>
                  No country data available.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={countryChartData}
                    layout="vertical"
                    margin={{ left: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="country" type="category"
                      width={100} fontSize={11}
                    />
                    <Tooltip
                      formatter={(v) => v.toLocaleString()}
                    />
                    <Bar dataKey="Paid" fill={GOLD} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Pie chart */}
            <div style={{
              background: '#fff', borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}>
              <h3 style={{
                color: NAVY, margin: '0 0 20px', fontSize: '16px'
              }}>
                Member Distribution by Country
              </h3>
              {countryChartData.length === 0 ? (
                <p style={{ color: '#888', textAlign: 'center' }}>
                  No data available.
                </p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={countryChartData}
                        dataKey="Paid"
                        nameKey="country"
                        cx="50%" cy="50%"
                        outerRadius={100}
                        label={({ country, percent }) =>
                          `${country} ${(percent * 100).toFixed(0)}%`
                        }
                        labelLine={false}
                        fontSize={11}
                      >
                        {countryChartData.map((_, i) => (
                          <Cell
                            key={i}
                            fill={CHART_COLORS[i % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v) => v.toLocaleString()}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── INDIVIDUAL TAB ── */}
        {activeTab === 'individuals' && (
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <h3 style={{
              color: NAVY, margin: '0 0 16px', fontSize: '16px'
            }}>
              Individual Member Drill-Down
            </h3>

            <input
              type="text"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setSelected(null);
              }}
              placeholder="🔍 Search by name, house or country..."
              style={{
                width: '100%', padding: '11px 14px',
                border: '2px solid #e0e0e0', borderRadius: '8px',
                fontSize: '14px', outline: 'none',
                boxSizing: 'border-box', marginBottom: '16px'
              }}
            />

            {!search.trim() ? (
              <p style={{
                color: '#888', textAlign: 'center',
                padding: '40px 0', fontSize: '14px'
              }}>
                Search for a member to see their dues history.
              </p>
            ) : filteredIndividuals.length === 0 ? (
              <p style={{
                color: '#888', textAlign: 'center',
                padding: '40px 0', fontSize: '14px'
              }}>
                No members found for "{search}".
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%', borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    {['Member','House','Year','Owed',
                      'Paid','Balance','Status'].map(h => (
                      <th key={h} style={{
                        padding: '10px 16px', textAlign: 'left',
                        color: '#888', fontWeight: '600',
                        fontSize: '12px', textTransform: 'uppercase',
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredIndividuals.map((row, i) => {
                    const balance = (row.owed || 0) - (row.paid || 0);
                    const settled = balance <= 0;
                    return (
                      <tr
                        key={i}
                        onClick={() =>
                          setSelected(selected === i ? null : i)
                        }
                        style={{
                          borderBottom: '1px solid #f0f0f0',
                          cursor: 'pointer',
                          background: selected === i
                            ? '#f0f4f8' : 'white'
                        }}
                      >
                        <td style={{
                          padding: '12px 16px',
                          fontWeight: '600', color: NAVY
                        }}>
                          {row.name}
                          {row.location && (
                            <div style={{
                              fontSize: '11px', color: '#aaa',
                              fontWeight: 'normal'
                            }}>
                              📍 {row.location}
                            </div>
                          )}
                        </td>
                        <td style={{
                          padding: '12px 16px', color: '#555'
                        }}>
                          {row.boardingHouse}
                        </td>
                        <td style={{
                          padding: '12px 16px', color: '#555'
                        }}>
                          {row.year}
                        </td>
                        <td style={{
                          padding: '12px 16px', color: '#333'
                        }}>
                          {(row.owed || 0).toLocaleString()}
                        </td>
                        <td style={{
                          padding: '12px 16px',
                          color: GREEN, fontWeight: '600'
                        }}>
                          {(row.paid || 0).toLocaleString()}
                        </td>
                        <td style={{
                          padding: '12px 16px',
                          color: settled ? GREEN : RED,
                          fontWeight: 'bold'
                        }}>
                          {balance.toLocaleString()}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            background: settled
                              ? '#d4edda' : '#fdecea',
                            color: settled ? GREEN : RED,
                            padding: '3px 8px', borderRadius: '8px',
                            fontSize: '11px', fontWeight: 'bold'
                          }}>
                            {settled ? '✓ Paid' : 'Outstanding'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}